import React from 'react';

import { formatDeductibleOrOop } from 'Containers/ResultPage/CompareModal/DeductiblesLimits/_shared';
import { usePremiumContext } from 'Contexts/premiumContext';
import { useTextContext } from 'Contexts/textContext';
import { FormattedTooltipProps, Icon, Numbers, NumberWithPayPeriod, Paragraph } from 'DesignLibrary/atoms';
import { PremiumPerDeduction } from 'DesignLibrary/atoms/PremiumPerDeduction';
import { useViewport } from 'DesignLibrary/context';
import { TooltipCaps } from 'DesignLibrary/molecules';
import Plan from 'Models/plan';
import RichText from 'Shared/RichText';
import Text from 'Shared/Text';
import {
  OopEstimateTooltipWithTooltip,
  OopMaxTooltipWithText,
  PremiumTooltipWithText,
  YearlyTotalTooltipWithText,
} from 'Utils/createTooltips';
import { toDollars } from 'Utils/helpers';

import { BreakdownTag, SplitThreshold, Value, ValuesContainer } from './styled';

interface BreakdownProps {
  med: JSX.Element | number;
  drug: JSX.Element | number;
  testId: string;
}

const Breakdown = ({ med, drug, testId }: BreakdownProps) => (
  <SplitThreshold data-testid={testId}>
    <div>
      {/* TODO: CONTENTFUL */}
      <BreakdownTag>Med</BreakdownTag>
      {med}
    </div>
    <div>
      {/* TODO: CONTENTFUL */}
      <BreakdownTag>Drug</BreakdownTag>
      {drug}
    </div>
  </SplitThreshold>
);

export interface ValuesProps {
  plan: Plan;
  isProviderSearchEnabled?: boolean;
  hsaBalance?: number;
}

export const Values = ({ plan, isProviderSearchEnabled = false, hsaBalance }: ValuesProps) => {
  const { retrieveContentfulData } = useTextContext();
  const { payPeriod } = usePremiumContext();
  const { device } = useViewport();

  // ------------------------
  // VALUES
  // ------------------------

  const estimatedYearlyTotal = plan.realCostBeforeTaxBenefit;
  const outOfPocketEstimate = plan.calcOutOfPocket();

  const hasComprehensiveDeductible =
    plan.getIsInNetworkDeductibleComprehensive() || plan.getIsCombinedNetworkDeductibleComprehensive();
  const hasComprehensiveOopMax =
    plan.getIsInNetworkOopLimitComprehensive() || plan.getIsCombinedNetworkOopLimitComprehensive();
  const {
    inNetworkDeductible,
    inNetworkMedDeductible,
    inNetworkDrugDeductible,
    inNetworkOopMax,
    inNetworkMedOopMax,
    inNetworkDrugOopMax,
    combinedNetworkDeductible, // Builder Indemnity network deductible when rx deductible type is 'Shared' or 'Independent'
    combinedNetworkMedDeductible, // Builder Indemnity network deductible when rx deductible type is 'None'
    combinedNetworkDrugDeductible,
    combinedNetworkOopMax, // Builder Indemnity network oop max
    combinedNetworkMedOopMax,
    combinedNetworkDrugOopMax,
  } = plan;

  const isOopGreaterThanOopMax =
    outOfPocketEstimate >
    (inNetworkOopMax || (inNetworkMedOopMax || 0) + (inNetworkDrugOopMax || 0) || Infinity);

  // ------------------------
  // TOOLTIPS
  // ------------------------

  const deductibleTooltip = {
    ...retrieveContentfulData<FormattedTooltipProps>('tool_tips.deductible'),
    note: hasComprehensiveDeductible ? null : (
      <Text
        vars={{
          medicalDeductible: toDollars(inNetworkMedDeductible),
          drugDeductible: toDollars(inNetworkDrugDeductible),
        }}
        field="tool_tips.deductible.note"
      />
    ),
  };

  // ------------------------
  // RENDERING VALUES
  // ------------------------

  // YEARLY
  const yearlyExpenseTotalContainer = (
    <Value data-testid="yearly-expense-total">
      <YearlyTotalTooltipWithText payPeriod={payPeriod} />
      <NumberWithPayPeriod
        value={estimatedYearlyTotal}
        payPeriodType="yearly"
        fontWeight="bold"
        size={device === 'mobile' ? 'medium' : 'large'}
        keyHint={`plan-${plan.planId}-yearly-expense-total`}
      />
    </Value>
  );

  // PREMIUM
  const premiumPerDeductionContainer = (
    <Value data-testid="premium">
      <PremiumTooltipWithText payPeriod={payPeriod} keyHint={`plan-${plan.planId}-premium`} />
      <PremiumPerDeduction
        annualPremium={plan.annualPremium}
        deductions={plan.annualPremiumDeductions}
        currency
        keyHint={`plan-${plan.planId}-premium-per-deduction`}
      />
    </Value>
  );

  // DEDUCTIBLE
  const renderDeductible = () => {
    const hasInNetworkDeductible = plan.hasInNetworkDeductible();
    const hasCombinedDeductible = plan.hasCombinedNetworkDeductible();

    if (!hasInNetworkDeductible && !hasCombinedDeductible) {
      return (
        <Numbers size="medium">
          <Text field="health_section.null_plan_detail_text.deductibleAndOopMax" />
        </Numbers>
      );
    }

    const deductible = hasCombinedDeductible ? combinedNetworkDeductible : inNetworkDeductible;
    const medicalDeductible = hasCombinedDeductible ? combinedNetworkMedDeductible : inNetworkMedDeductible;
    const drugDeductible = hasCombinedDeductible ? combinedNetworkDrugDeductible : inNetworkDrugDeductible;
    const hasComprehensiveDeductible = hasCombinedDeductible
      ? plan.getIsCombinedNetworkDeductibleComprehensive()
      : plan.getIsInNetworkDeductibleComprehensive();
    const testId = hasCombinedDeductible ? 'combined-network-deductible' : 'in-network-deductible';

    if (hasComprehensiveDeductible) {
      return (
        <Numbers size="medium" data-testid={testId}>
          {toDollars(deductible)}
        </Numbers>
      );
    }

    return (
      <Breakdown
        testId={testId}
        med={formatDeductibleOrOop(medicalDeductible)}
        drug={formatDeductibleOrOop(drugDeductible)}
      />
    );
  };

  const deductibleContainer = (
    <Value data-testid="deductible">
      <TooltipCaps tooltip={deductibleTooltip} keyHint={`plan-${plan.planId}-deductible`}>
        <Text field="health_section.recommendation.deductible" />
      </TooltipCaps>
      {renderDeductible()}
    </Value>
  );

  // OOP ESTIMATE
  const oopGreaterThanOopMaxTooltip = isOopGreaterThanOopMax && (
    <div data-testid="oop-greater-than">
      <TooltipCaps
        tooltip={retrieveContentfulData<FormattedTooltipProps>(
          'tool_tips.yearly_estimate_greater_than_out_of_pocket_limit',
        )}
        keyHint={`plan-${plan.planId}-yearly-estimate-greater-than-oop`}
      >
        <Icon type="WarningCircle" />
      </TooltipCaps>
    </div>
  );

  const outOfPocketEstimateContainer = (
    <Value data-testid="out-of-pocket-estimate">
      {hsaBalance !== undefined && hsaBalance > 0 && outOfPocketEstimate > 0 ? (
        <>
          <div style={{ display: 'flex', alignItems: 'baseline', gap: '8px', flexWrap: 'wrap' }}>
            <NumberWithPayPeriod
              value={outOfPocketEstimate}
              payPeriodType="yearly"
              keyHint={`plan-${plan.planId}-oop-estimate-value`}
            />
            <OopEstimateTooltipWithTooltip keyHint={`plan-${plan.planId}-oop-estimate`} />
            {oopGreaterThanOopMaxTooltip}
          </div>
          <Paragraph size="mini" color="--text-gray" data-testid="hsa-balance" style={{ order: 3 }}>
            <RichText
              field="health_section.recommendation.hsa_balance"
              vars={[toDollars(hsaBalance)]}
              inlineParagraphs={false}
            />
          </Paragraph>
        </>
      ) : (
        <>
          <OopEstimateTooltipWithTooltip keyHint={`plan-${plan.planId}-oop-estimate`} />
          <NumberWithPayPeriod
            value={outOfPocketEstimate}
            payPeriodType="yearly"
            keyHint={`plan-${plan.planId}-oop-estimate-value`}
          />
          {oopGreaterThanOopMaxTooltip}
        </>
      )}
    </Value>
  );

  // OOP MAX
  const renderOutOfPocketMaximum = () => {
    const hasCombinedOopMax = plan.hasCombinedNetworkOopMax();
    const hasNoInNetworkOopMax =
      (inNetworkOopMax === 0 && (inNetworkMedOopMax || 0) + (inNetworkDrugOopMax || 0) === 0) ||
      inNetworkOopMax === 999999;

    if (hasNoInNetworkOopMax && !hasCombinedOopMax) {
      return (
        <Paragraph color="--text-color">
          <Text field="health_section.recommendation.unlimited" />;
        </Paragraph>
      );
    }

    const oopMax = hasCombinedOopMax ? combinedNetworkOopMax : inNetworkOopMax;
    const medOopMax = hasCombinedOopMax ? combinedNetworkMedOopMax : inNetworkMedOopMax;
    const drugOopMax = hasCombinedOopMax ? combinedNetworkDrugOopMax : inNetworkDrugOopMax;
    const hasComprehensiveOopMax = hasCombinedOopMax
      ? plan.getIsCombinedNetworkOopLimitComprehensive()
      : plan.getIsInNetworkOopLimitComprehensive();
    const testId = hasCombinedOopMax ? 'combined-network-oop-max' : 'in-network-oop-max';

    if (hasComprehensiveOopMax) {
      return (
        <Numbers size="medium" data-testid={testId}>
          {toDollars(oopMax)}
        </Numbers>
      );
    }

    return (
      <Breakdown
        testId={testId}
        med={formatDeductibleOrOop(medOopMax)}
        drug={formatDeductibleOrOop(drugOopMax)}
      />
    );
  };

  const outOfPocketMaximumContainer = (
    <Value>
      <OopMaxTooltipWithText
        hasComprehensiveOopMax={hasComprehensiveOopMax}
        medicalOopMax={inNetworkMedOopMax || 0}
        drugOopMax={inNetworkDrugOopMax || 0}
        keyHint={`plan-${plan.planId}-oop-max`}
      />
      {renderOutOfPocketMaximum()}
    </Value>
  );

  return (
    <ValuesContainer isProviderSearchEnabled={isProviderSearchEnabled}>
      {yearlyExpenseTotalContainer}
      {premiumPerDeductionContainer}
      {deductibleContainer}
      {outOfPocketEstimateContainer}
      {outOfPocketMaximumContainer}
    </ValuesContainer>
  );
};
